import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FormContainer from "../../../components/FormContainer";
import BookingService from '../../../models/BookingService';
import Workorder from '../../../models/Workorder';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { getPathObject, pageToolbarHeight, STATUS_BOOK_SERV_CANCELLED, STATUS_BOOK_SERV_COMPLETED, STATUS_BOOK_SERV_NEW, STATUS_BOOK_SERV_VHC_COMPLETED } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { NotificationType, SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import {
    Edit as EditIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Receipt as ReceiptIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import NotificationDialog from '../../../components/NotificationDialog';
import GridViewEdit from '../../../components/GridViewEdit';
import { validate as uuidValidate } from "uuid";

const groupName: MenuGroupName = "Servis";
const subgroupName: string = "Transaksi";
const code: MenuCode = "SERVICE-BOOKING-WO";
const title: MenuTitle = "Workorder";
const endpoint = "workorder";
const bookingEndpoint = "book";
const invoiceEndpoint = "invoice";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [error, setError] = useState<NotificationType | null>(null);
    const [success, setSuccess] = useState<NotificationType | null>(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<BookingService>(({} as BookingService));
    const [bookingServiceToCancel, setBookingServiceToCancel] = useState<BookingService | undefined>(undefined);
    const [detailWorkorderToEdit, setDetailWorkorderToEdit] = useState<Workorder | null>(null);
    const [detailWorkorderToView, setDetailWorkorderToView] = useState<Workorder | null>(null);
    const [validationResult, setValidationResult] = useState<any[] | null>(null);
    const navigate = useNavigate();
    const workorderRef = useRef();
    
    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

    const loadData = useCallback(() => {
		if (!location?.state?.ucode || !apiCaller)
            return;

        setLoading(true);
		apiCaller.get(`${endpoint}/book/id?ucode=${location.state.ucode}`)
            .then((response) => setFormData({...response.data?.data}))
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [location.state, apiCaller]);

	useEffect(() => {
		loadData();
	}, [location.state]);

    const onValidate = useCallback((data: Workorder | undefined) => {
        return true;
    }, []);

    const onCancelBooking = useCallback(() => {
        if (!bookingServiceToCancel || !apiCaller)
            return;

        setLoading(true);

        apiCaller.put(`${bookingEndpoint}/cancel-booking?ucode=${bookingServiceToCancel.ucode}`)
            .then(() => {
                setLoading(false);
                setBookingServiceToCancel(undefined);
                loadData();
            })
            .catch((error) => {
                console.error({error});
                setBookingServiceToCancel(undefined);
                setError({
                    title: "Error!",
                    message: "Pembatalan booking service gagal dilakukan. Silakan hubungi administrator.",
                });
                setLoading(false);
            })
        ;
    }, []);

    const onClose = useCallback(() => {
        const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING");

        if (!pathObject?.mainPath || !pathObject?.path)
            return;

        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Workorder> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        const apiCallerObj = param.data.ucode ? apiCaller.put(`${endpoint}`, JSON.stringify(param.data)) : apiCaller.post(`${endpoint}`, JSON.stringify(param.data));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    useEffect(() => {
        if (!detailWorkorderToEdit)
            return;

        const pathObject = getPathObject(groupName, subgroupName, "SERVICE-WORKORDER");

        if (!pathObject?.mainPath || !pathObject?.formPath)
        {
            setDetailWorkorderToEdit(null);
            return;
        }

        const dataToSend = {
            ...detailWorkorderToEdit,
            ucode_booking_service: formData?.ucode,
            no_booking_service: formData?.no_transaksi,
            tanggal_booking_service: formData?.tanggal_transaksi,
            nama_customer: formData?.nama_customer,
            nopol: detailWorkorderToEdit?.nopol,
            jenis_kendaraan: detailWorkorderToEdit?.jenis_kendaraan,
            keterangan_operator: formData?.keterangan_operator,
            list_workorder_brg: detailWorkorderToEdit.list_workorder_brg?.map(d => {
                return {
                    ...d,
                    brg: {
                        ucode: d.ucode_brg,
                        kode: d.kode_brg,
                        nama: d.nama_brg,
                    }
                }
            }),
            list_workorder_karyawan: detailWorkorderToEdit.list_workorder_karyawan?.map(d => {
                return {
                    ...d,
                    karyawan: {
                        ucode: d.ucode_karyawan,
                        kode: d.kode_karyawan,
                        nama: d.nama_karyawan,
                    }
                }
            }),
        };
        
        navigate(`${pathObject?.mainPath}${pathObject?.formPath}`, {state: dataToSend});
    }, [detailWorkorderToEdit]);

    useEffect(() => {
        if (!detailWorkorderToView)
            return;

        const pathObject = getPathObject(groupName, subgroupName, "SERVICE-WORKORDER");

        if (!pathObject?.mainPath || !pathObject?.formPath)
        {
            setDetailWorkorderToEdit(null);
            return;
        }

        const dataToSend = {
            ...detailWorkorderToView,
            ucode_booking_service: formData?.ucode,
            no_booking_service: formData?.no_transaksi,
            tanggal_booking_service: formData?.tanggal_transaksi,
            nama_customer: formData?.nama_customer,
            nopol: detailWorkorderToView?.nopol,
            jenis_kendaraan: detailWorkorderToView?.jenis_kendaraan,
            keterangan_operator: formData?.keterangan_operator,
            list_workorder_brg: detailWorkorderToView.list_workorder_brg?.map(d => {
                return {
                    ...d,
                    brg: {
                        ucode: d.ucode_brg,
                        kode: d.kode_brg,
                        nama: d.nama_brg,
                    }
                }
            }),
            list_workorder_karyawan: detailWorkorderToView.list_workorder_karyawan?.map(d => {
                return {
                    ...d,
                    karyawan: {
                        ucode: d.ucode_karyawan,
                        kode: d.kode_karyawan,
                        nama: d.nama_karyawan,
                    }
                }
            }),
            isViewMode: true,
        };
        
        navigate(`${pathObject?.mainPath}${pathObject?.formPath}`, {state: dataToSend});
    }, [detailWorkorderToView]);
        
    const handleGenerateInvoice = useCallback((dataToCompare?: Workorder) => {
        if (!apiCaller || !dataToCompare)
            return;

        setLoading(true);

        apiCaller.put(`${invoiceEndpoint}/print-invoice?id=${dataToCompare.ucode_booking_service_kendaraan}`)
            .then(() => {
                setLoading(false);
                loadData();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, loadData]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
            >
                <DataForm<BookingService>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
                    hideDefaultCommands
                    customFormCommands={[
                        {
                            title: "Batalkan Booking Service",
                            action: (rowData?: BookingService) => setBookingServiceToCancel(rowData),
                            icon: CancelIcon,
                            color: "delete",
                            disabled: formData.kode_status !== STATUS_BOOK_SERV_NEW && (formData.kode_status === STATUS_BOOK_SERV_COMPLETED || formData.kode_status === STATUS_BOOK_SERV_CANCELLED),
                        },
                    ]}
                    fields={[
                        {
                            name: "no_transaksi",
                            title: "No. Booking Service",
                            inputType: "text",
                            dataType: (typeof formData.no_transaksi),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "tanggal_transaksi",
                            title: "Tanggal Booking Service",
                            inputType: "date",
                            dataType: (typeof formData.tanggal_transaksi),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "nama_customer",
                            title: "Customer",
                            inputType: "text",
                            dataType: "string",
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "keterangan_operator",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: "string",
                            isReadonly: true,
                            isRequired: false,
                            rows: 3,
                        }
                    ]}
                    onValidate={onValidate}
                    onClose={() => onClose()}
                    onSave={(data) => onSave(data)}
                />
                <Box
                    sx={{
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                        width: "100%",
                        height: `${pageToolbarHeight}px`,
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "start",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            borderBottom: "2px solid #D9E7E5",
                            color: "#185E91",
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography variant="button" sx={{fontSize: "1rem"}}>Detail Kendaraan</Typography>
                    </Box>
                </Box>
                <GridViewEdit<Workorder>
                    id_field="ucode"
                    columns={[
                        {
                            columnName: "action",
                            columnTitle: "",
                            columnType: "action",
                            actions: [
                                {
                                    title: "Ubah",
                                    color: "primary",
                                    action: (rowData: Workorder) => setDetailWorkorderToEdit({ ...rowData }),
                                    icon: EditIcon,
                                    variant: "outlined",
                                    disabled: (rowData: Workorder) => rowData?.kode_status_booking_service_kendaraan === STATUS_BOOK_SERV_VHC_COMPLETED,
                                },
                                {
                                    title: "Lihat",
                                    color: "primary",
                                    action: (rowData: Workorder) => setDetailWorkorderToView({ ...rowData }),
                                    icon: SearchIcon,
                                    variant: "outlined",
                                    disabled: (rowData: Workorder) => rowData?.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_COMPLETED,
                                },
                                {
                                    title: "Generate Invoice",
                                    color: "primary",
                                    action: (rowData: Workorder) => handleGenerateInvoice(rowData),
                                    icon: ReceiptIcon,
                                    variant: "outlined",
                                    disabled: (rowData: Workorder) => rowData?.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_COMPLETED || rowData.invoice_generated,
                                },
                                {
                                    title: "Cetak Invoice",
                                    color: "primary",
                                    action: (rowData: Workorder) => {},
                                    icon: ReceiptIcon,
                                    variant: "outlined",
                                    disabled: (rowData: Workorder) => rowData?.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_COMPLETED || !rowData.invoice_generated,
                                },
                            ],
                            width: "10%",
                        },
                        {
                            columnName: "nopol",
                            columnTitle: "No. Polisi",
                            columnType: "data",
                            titleColumn: true,
                        },
                        {
                            columnName: "jenis_kendaraan",
                            columnTitle: "Jenis Kendaraan",
                            columnType: "data",
                        },
                        {
                            columnName: "nama_status_booking_service_kendaraan",
                            columnTitle: "Status",
                            columnType: "data",
                        },
                    ]}
                    data={formData.list_workorder}
                    containerStyle={{
                        marginTop: "0.2rem",
                    }}
                />
            </FormContainer>
        {
            bookingServiceToCancel ?
            <NotificationDialog
                message={`Apakah Anda yakin akan membatalkan booking service "${bookingServiceToCancel.no_transaksi}"?`}
                onClose={() => setBookingServiceToCancel(undefined)}
                onConfirm={() => onCancelBooking()}
                title="Konfirmasi"
                confirmButtonText="Ya"
                closeButtonText="Batal"
                warning
            />
            :
            null
        }
        {
            error ?
            <NotificationDialog
                message={error.message}
                onClose={() => setError(null)}
                title={error.title}
                closeButtonText="Tutup"
                warning
            />
            :
            null
        }
        </>
    );
};

export default Form;
